// The motion language in code (docs/MOTION.md). Framer variants use these;
// continuous rAF surfaces use the Spring class. No other spring values exist.

import type { Transition } from "framer-motion";

export const springUi: Transition = { type: "spring", stiffness: 500, damping: 34, mass: 1 };
export const springPanel: Transition = { type: "spring", stiffness: 320, damping: 30, mass: 1 };
export const springPlayful: Transition = { type: "spring", stiffness: 420, damping: 22, mass: 0.9 };
export const springSettle: Transition = { type: "spring", stiffness: 320, damping: 26, mass: 1 };

// Aligned one-to-one with the motion tokens in tokens.css.
export const durations = { fast: 0.13, base: 0.22, slow: 0.36, cinematic: 0.64 } as const;
export const easeOut = [0.16, 1, 0.3, 1] as const;
export const easeIn = [0.4, 0, 1, 1] as const;
export const easeThrough = [0.65, 0, 0.35, 1] as const;

export function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Common enter/exit variants for panels and popovers. */
export const popIn = {
  initial: { opacity: 0, scale: 0.96, y: 4 },
  animate: { opacity: 1, scale: 1, y: 0, transition: springUi },
  exit: { opacity: 0, scale: 0.98, y: 2, transition: { duration: durations.fast, ease: easeIn } },
};

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: durations.base, ease: easeOut } },
  exit: { opacity: 0, transition: { duration: durations.fast, ease: easeIn } },
};

export const riseIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: springPanel },
  exit: { opacity: 0, y: 8, transition: { duration: durations.fast, ease: easeIn } },
};

/** A menu or dropdown unfolding from its trigger edge (set transform-origin). */
export const menuUnfold = {
  initial: { opacity: 0, scale: 0.97, y: -4 },
  animate: { opacity: 1, scale: 1, y: 0, transition: springUi },
  exit: { opacity: 0, scale: 0.98, y: -2, transition: { duration: durations.fast, ease: easeIn } },
};

/** A side panel or drawer gliding in from an edge. `x`/`y` set the offset. */
export const drawer = {
  initial: (offset: { x?: number; y?: number }) => ({ opacity: 0, ...offset }),
  animate: { opacity: 1, x: 0, y: 0, transition: springPanel },
  exit: (offset: { x?: number; y?: number }) => ({ opacity: 0, ...offset, transition: { duration: durations.base, ease: easeIn } }),
};

/** A selection outline that morphs between targets (use with `layoutId`). */
export const selectionMorph: Transition = { type: "spring", stiffness: 520, damping: 40, mass: 0.8 };

/**
 * A critically tunable spring for continuous rAF surfaces (faders, camera,
 * meters). step() advances by dt seconds toward target.
 */
export class Spring {
  value: number;
  velocity = 0;
  target: number;

  constructor(
    initial: number,
    private stiffness = 320,
    private damping = 26,
    private mass = 1,
  ) {
    this.value = initial;
    this.target = initial;
  }

  step(dt: number): number {
    const f = -this.stiffness * (this.value - this.target);
    const d = -this.damping * this.velocity;
    const a = (f + d) / this.mass;
    this.velocity += a * dt;
    this.value += this.velocity * dt;
    return this.value;
  }

  /** Snap without animation (reduced motion, initialization). */
  snap(v: number): void {
    this.value = v;
    this.target = v;
    this.velocity = 0;
  }

  get settled(): boolean {
    return Math.abs(this.value - this.target) < 0.0005 && Math.abs(this.velocity) < 0.0005;
  }
}

/** Exponential decay used for camera inertia (0.92 per frame at 60 fps). */
export function decay(velocity: number, dt: number, factorPerFrame = 0.92): number {
  return velocity * Math.pow(factorPerFrame, dt * 60);
}
